import {useLocation} from '@github-ui/react-router'
import {useEffect} from 'react'

/**
 * Unique ID counter for store instances.
 */
let storeId = 0

/**
 * Module-level store for ephemeral focus hints.
 *
 * Hints are set before navigation and consumed on the destination page.
 * They automatically become invalid after subsequent navigation.
 */
let store: {
  id: number
  hint: string | null
  context?: unknown
  setAt: string
  consumedAt: string | null
} | null = null

function getLocationKey(): string {
  if (typeof window === 'undefined') return ''
  return window.location.pathname + window.location.search
}

/**
 * Set a focus hint to be consumed on the next navigation.
 *
 * Call this BEFORE navigating (e.g., in an onClick handler before navigate()).
 * The hint will be available on the destination page via useFocusHint().
 *
 * @example
 * function MyComponent() {
 *   const navigate = useNavigate()
 *
 *   return (
 *     <button onClick={() => {
 *       setFocusHint('my-button')
 *       navigate('/next-page')
 *     }}>
 *       Go to next page
 *     </button>
 *   )
 * }
 */
export function setFocusHint(hint: string | null, context?: unknown): void {
  storeId++
  store = {
    id: storeId,
    hint,
    context,
    setAt: getLocationKey(),
    consumedAt: null,
  }
}

/**
 * Result type from useFocusHint hook.
 */
export interface FocusHintResult {
  /** The focus hint for this page, or null if none */
  focusHint: string | null
  /** Optional context data passed with the hint */
  context?: unknown
}

/**
 * Read the current focus hint.
 *
 * Returns the hint if one was set on the previous page, null otherwise.
 * The hint is ephemeral - valid only for the first navigation after
 * it was set, and automatically cleared on subsequent navigations.
 *
 * @example
 * function DestinationPage() {
 *   const { focusHint } = useFocusHint()
 *
 *   return (
 *     <button data-react-autofocus={focusHint === 'my-button' ? true : null}>
 *       This will be focused if hint matches
 *     </button>
 *   )
 * }
 */
export function useFocusHint(): FocusHintResult {
  const location = useLocation()
  const currentLocation = location.pathname + location.search

  // Capture the store ID at render time to ensure effects only
  // operate on the correct store (not stale from previous renders)
  const currentStoreId = store?.id ?? null

  // Track consumption in an effect, but only for the current store
  useEffect(() => {
    // If store doesn't exist or its ID doesn't match, skip
    if (!store || store.id !== currentStoreId) return

    if (store.setAt !== currentLocation) {
      if (store.consumedAt === null) {
        // Mark as consumed at this location
        store.consumedAt = currentLocation
      } else if (store.consumedAt !== currentLocation) {
        // Navigated away from consumption location, clear store
        store = null
      }
    }
  }, [currentLocation, currentStoreId])

  if (!store) {
    return {focusHint: null, context: undefined}
  }

  // Not valid on the page where it was set
  if (store.setAt === currentLocation) {
    return {focusHint: null, context: undefined}
  }

  // If consumed at a different location, no longer valid
  if (store.consumedAt !== null && store.consumedAt !== currentLocation) {
    return {focusHint: null, context: undefined}
  }

  return {focusHint: store.hint, context: store.context}
}

/**
 * Clear any pending focus hint. Useful for testing or cleanup.
 */
export function clearFocusHint(): void {
  store = null
}
